import { Injectable } from '@angular/core';
import { SignatureField, SignatureFieldContext } from '../../interfaces/signature';
import { ApplicationStoreService } from '../application-store.service';

type SerializedSignatureContext = {
  providerName: string;
  controls: {
    contentControlId: number;
    field: SignatureField;
  }[];
};

@Injectable({ providedIn: 'root' })
export class ContentControlToSignatureFieldService {
  private static FIELD_CONTEXTS_KEY = 'kp-signature-fields-v1';

  constructor(private applicationStoreService: ApplicationStoreService) {}

  get(contentControlId: number): SignatureFieldContext | undefined {
    const serializedContext = this.getSerializedContext();
    if (!serializedContext) {
      return undefined;
    }

    const found = serializedContext.controls.find((sfc) => sfc.contentControlId === contentControlId);
    return found
      ? {
          contentControlId: found.contentControlId,
          providerName: serializedContext.providerName,
          field: found.field,
        }
      : undefined;
  }

  getAll(contentControlIds: number[]): SignatureFieldContext[] {
    const serializedContext = this.getSerializedContext();
    if (!serializedContext) {
      return [];
    }

    const results: SignatureFieldContext[] = [];
    contentControlIds.forEach((id) => {
      const found = serializedContext.controls.find((sfc) => sfc.contentControlId === id);
      if (found) {
        results.push({
          contentControlId: found.contentControlId,
          providerName: serializedContext.providerName,
          field: found.field,
        });
      }
    });
    return results;
  }

  filter(contentControls: Word.ContentControl[]): Word.ContentControl[] {
    const serializedContext = this.getSerializedContext();
    if (!serializedContext) {
      return [];
    }

    const isPresent = (contentControlId: number): boolean =>
      serializedContext.controls.find((sfc) => sfc.contentControlId === contentControlId) !== undefined;

    return contentControls.filter((wcc) => isPresent(wcc.id));
  }

  async set(contentControlId: number, providerName: string, field: SignatureField): Promise<void> {
    let serializedContext = this.getSerializedContext();
    if (!serializedContext) {
      serializedContext = { providerName: providerName, controls: [] };
    }

    const idx = serializedContext.controls.findIndex((sc) => sc.contentControlId === contentControlId);
    if (idx > -1) {
      serializedContext.controls.splice(idx, 1);
    }

    serializedContext.controls.push({
      contentControlId: contentControlId,
      field: field,
    });

    await this.setSerializedContext(serializedContext);
  }

  async delete(contentControlId: number): Promise<boolean> {
    const serializedContext = this.getSerializedContext();

    // if there are no signature fields
    if (!serializedContext) {
      return false;
    }

    const idx = serializedContext.controls.findIndex((sc) => sc.contentControlId === contentControlId);
    if (idx > -1) {
      serializedContext.controls.splice(idx, 1);
      await this.setSerializedContext(serializedContext);
      return true;
    }

    return false;
  }

  private getSerializedContext(): SerializedSignatureContext {
    return this.applicationStoreService.get(ContentControlToSignatureFieldService.FIELD_CONTEXTS_KEY) as SerializedSignatureContext;
  }

  private setSerializedContext(serializedContext: SerializedSignatureContext): Promise<void> {
    return this.applicationStoreService.set(ContentControlToSignatureFieldService.FIELD_CONTEXTS_KEY, serializedContext);
  }
}
